import React from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '../HomePage/useSEO';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import { Button } from '@/Components/ui/button';
import {
  MapPin,
  Mail,
  Globe,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Quote,
} from 'lucide-react';

/**
 * About / Company page — restyled to match the SearchJob.ai landing
 * theme: cream background, forest-green primary, gradient heading
 * accents (primary → emerald → teal), eyebrow pills, rounded-3xl
 * cards, pill CTAs, and dark-ink bands for the stats + final CTA
 * (the same surface as the landing's Security band and Final CTA).
 *
 * All copy is preserved from the original page; only the presentation
 * was brought in line with the landing page.
 */

const INK = 'hsl(164 33% 7%)'; // brand dark-ink surface (Security / CTA bands)

export default function AboutUs() {
  const navigate = useNavigate();

  useSEO({
    title: 'About SearchJob.ai — AI Recruitment Platform Built for the AI Era',
    description:
      'SearchJob.ai is a Gen-AI recruitment and candidate-assessment platform. Learn how we help companies hire faster, smarter and more fairly — with the human touch.',
    canonical: 'https://searchjob.ai/hirewithus/about',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  const keyFeatures = [
    'End-to-end AI-assisted recruitment — complete workflow automation from sourcing to onboarding',
    'AI sourcing engine — find best-fit talent faster and at scale with intelligent matching',
    'AI screening & video interviews — automate candidate evaluation with accuracy and consistency',
    'Interview management platform — centralised scheduling, collaboration and candidate tracking',
    'Assisted human interview — AI-powered insights that guide interviewers to better decisions',
  ];

  const services = [
    'Intelligent candidate profile scoring — data-driven scoring to shortlist top profiles',
    'Candidate mindset mapping — behavioural insight to find the right fit',
    'Skill screening assessments — customised tests for technical and domain skills',
    'AI-based interviews — automated video and coding interviews with real-time analysis',
    'AI-assisted traditional recruitment & staffing services',
  ];

  const whyUs = [
    {
      title: 'Superior automation for end-to-end interview management',
      desc: 'Streamlined workflow from candidate screening to final selection.',
    },
    {
      title: 'Role-based screening evaluations & comprehensive analytics',
      desc: 'Data-driven insights for better hiring decisions and process optimisation.',
    },
    {
      title: 'Exceptional candidate experience & unbiased assessments',
      desc: 'A fair and transparent evaluation process for every candidate.',
    },
  ];

  const testimonials = [
    {
      quote:
        'The SearchJob team is very competent and well staffed to handle any surge in hiring needs. They have unique technology to match the right candidates with the job requirements. They provide white-gloved treatment and constantly maintain good communication with the candidate until they join and settle in the job.',
      who: 'Founder & CEO — Grand Gaming',
    },
    {
      quote:
        'Working with SearchJob.ai has been a game-changer for us at Kinzy Club. As a growing brand, finding the right talent quickly and effectively is crucial — and their team delivered beyond expectations. From understanding our unique culture to sourcing candidates who not only had the right skills but also the right attitude, their attention to detail and commitment were unmatched.',
      who: 'Co-Founder, Kinzy Club',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        {/* ═════════════ HERO ═════════════ */}
        <section className="relative overflow-hidden bg-background isolate">
          {/* Gradient mesh + grid, matching the landing hero */}
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl" />
            <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-[-25%] left-1/2 -translate-x-1/2 w-[70%] h-[50%] bg-teal-500/10 rounded-full blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              color: 'hsl(var(--foreground) / 0.1)',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
            }}
          />

          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              About SearchJob.ai
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-foreground leading-[1.08] mb-6">
              Where technology meets
              <br />
              <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                talent acquisition.
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
              Revolutionising recruitment through AI innovation — the
              next-generation, Gen-AI-powered platform for identifying,
              evaluating and hiring top talent, with the human touch.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/hirewithus/signup')}
                className="!bg-primary !text-primary-foreground hover:!bg-primary/90 h-12 px-8 text-base font-semibold group !rounded-full shadow-lg shadow-primary/25"
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/hirewithus/contact')}
                className="h-12 px-8 text-base font-semibold !rounded-full bg-card/70 backdrop-blur-md"
              >
                Talk to us
              </Button>
            </div>
          </div>
        </section>

        {/* ═════════════ OVERVIEW ═════════════ */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow>Overview</Eyebrow>
              <SectionHeading plain="The recruitment platform" accent="built for the AI era." />
              <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                SearchJob.ai is building the next-generation, Gen-AI-powered
                recruitment and candidate-assessment platform designed to
                transform how organisations identify, evaluate and hire top
                talent — with the human touch.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Our mission is to bridge the gap between traditional hiring
                processes and the evolving demands of modern workplaces by
                leveraging artificial intelligence, automation and
                data-driven insights.
              </p>
            </div>
            <ImageCard src="/ai11.jpg" alt="Team collaboration and hiring process" />
          </div>
        </Section>

        {/* ═════════════ KEY FEATURES ═════════════ */}
        <Section muted>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ImageCard
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80"
              alt="AI-powered recruitment technology"
              className="lg:order-2"
            />
            <div className="lg:order-1">
              <Eyebrow>What we build</Eyebrow>
              <SectionHeading plain="Key features that" accent="do the heavy lifting." />
              <CheckList items={keyFeatures} />
            </div>
          </div>
        </Section>

        {/* ═════════════ SERVICES ═════════════ */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow>Our services</Eyebrow>
              <SectionHeading plain="Comprehensive AI-powered" accent="recruitment solutions." />
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                We transform how organisations identify, evaluate and hire top
                talent — end to end.
              </p>
              <CheckList items={services} />
            </div>
            <ImageCard
              src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=1200&q=80"
              alt="AI and technology innovation"
            />
          </div>
        </Section>

        {/* ═════════════ WHY CHOOSE US ═════════════ */}
        <Section muted>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Why choose us</Eyebrow>
            <SectionHeading center plain="Hire faster and smarter —" accent="without the trade-offs." />
            <p className="text-lg text-muted-foreground leading-relaxed">
              We help organisations overcome the shortcomings of current hiring
              practices — which take weeks and heavy spend — with comprehensive,
              unbiased and efficient candidate evaluations at every stage.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {whyUs.map((w, i) => (
              <div
                key={w.title}
                className="bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-emerald-800 text-primary-foreground flex items-center justify-center font-black text-lg shadow-lg shadow-primary/25 mb-4">
                  {i + 1}
                </div>
                <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                  {w.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {w.desc}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* ═════════════ STATS BAND (dark ink) ═════════════ */}
        <section className="relative py-14 lg:py-20 overflow-hidden" style={{ backgroundColor: INK }}>
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-2/3 h-1/2 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              {[
                { v: '2×', l: 'Faster recruiting' },
                { v: '30%', l: 'Time reduction in hiring' },
                { v: '20%', l: 'Higher retention rate' },
              ].map((s) => (
                <div key={s.l}>
                  <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    {s.v}
                  </div>
                  <div className="mt-2 text-sm text-white/70">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════════ TESTIMONIALS + CONTACT ═════════════ */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <Eyebrow>Get in touch</Eyebrow>
              <SectionHeading plain="Ready to experience the" accent="future of hiring?" />
              <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                Traditional recruitment can&apos;t keep pace with today&apos;s
                talent landscape. With AI-driven assessments, immersive
                interview journeys and data-backed insights, we help you
                discover the right candidates faster and smarter.
              </p>
              <div className="space-y-3 mt-6">
                <ContactLine icon={MapPin} text="Bangalore, India" />
                <ContactLine icon={Mail} text="contact@searchjob.ai" />
                <ContactLine icon={Globe} text="www.searchjob.ai" />
              </div>
            </div>
            <div className="space-y-5">
              {testimonials.map((t) => (
                <div
                  key={t.who}
                  className="relative bg-card border border-border/70 rounded-3xl p-6 shadow-sm"
                >
                  <Quote className="w-8 h-8 text-primary/15 mb-2" fill="currentColor" />
                  <p className="text-sm text-foreground/85 leading-relaxed italic mb-4">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <p className="text-sm font-semibold text-foreground">{t.who}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ═════════════ FUTURE OUTLOOK + TRENDS ═════════════ */}
        <Section muted>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ImageCard
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80"
              alt="Future technology and innovation"
              className="lg:order-2"
            />
            <div className="lg:order-1">
              <Eyebrow>Future outlook</Eyebrow>
              <SectionHeading plain="A talent-intelligence" accent="ecosystem for the future." />
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                We are building integrations with applicant tracking systems,
                HR management tools and collaboration platforms — enabling
                seamless workflow adoption for enterprises. On a mission to help
                companies hire faster, smarter and more fairly, while giving
                candidates a transparent and engaging evaluation experience.
              </p>
              <div className="rounded-3xl border border-primary/25 bg-primary/5 p-6">
                <p className="text-base text-foreground leading-relaxed">
                  <strong className="text-primary">
                    SearchJob is not just a hiring tool
                  </strong>{' '}
                  — it&apos;s a complete AI-driven talent-intelligence ecosystem
                  for the future of recruitment.
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center mt-16">
            <Eyebrow center>Market trend</Eyebrow>
            <SectionHeading center plain="AI in hiring is" accent="already the norm." />
            <div className="space-y-4 text-left">
              <p className="text-base text-muted-foreground leading-relaxed">
                Over 40% of companies already use AI in recruitment, with
                adoption growing rapidly. AI-driven interviews improve efficiency
                by 50–60%, reduce time-to-hire by 40%, and cut hiring costs by
                30–60%.
              </p>
              <p className="text-base text-muted-foreground leading-relaxed">
                We provide 360-degree candidate assessments — technical skills,
                problem-solving, communication, behavioural traits and cognitive
                aptitude — benchmarked against historical hiring data for clear,
                data-backed recommendations.
              </p>
            </div>
          </div>
        </Section>

        {/* ═════════════ FINAL CTA (dark ink) ═════════════ */}
        <section className="relative py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="relative overflow-hidden rounded-[2.5rem] p-10 lg:p-16 text-white shadow-2xl text-center"
              style={{ backgroundColor: INK }}
            >
              <div className="absolute top-[-30%] right-[-10%] w-[60%] h-[80%] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-[-30%] left-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative max-w-2xl mx-auto">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-tight">
                  Ready to experience the
                  <br />
                  <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    future of hiring?
                  </span>
                </h2>
                <p className="text-lg text-white/70 leading-relaxed mb-8">
                  Transform your recruitment with AI-powered solutions that
                  deliver faster, smarter and more effective hiring.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    size="lg"
                    onClick={() => navigate('/hirewithus/contact')}
                    className="!bg-white !text-foreground hover:!bg-white/90 h-12 px-8 text-base font-semibold group shadow-xl !rounded-full"
                  >
                    Contact Us
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate('/hirewithus/signup')}
                    className="!border-white/30 !bg-transparent !text-white hover:!bg-white/10 h-12 px-8 text-base font-semibold !rounded-full"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Small shared bits — mirror the landing page's building blocks    */
/* ─────────────────────────────────────────────────────────────── */
function Section({ children, muted = false }) {
  return (
    <section
      className={`relative py-14 lg:py-20 overflow-hidden ${
        muted ? 'bg-muted/30' : 'bg-background'
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </section>
  );
}

function Eyebrow({ children, center = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4 ${
        center ? 'mx-auto' : ''
      }`}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {children}
    </div>
  );
}

function SectionHeading({ plain, accent, center = false }) {
  return (
    <h2
      className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground leading-[1.1] mb-5 ${
        center ? 'text-center' : ''
      }`}
    >
      {plain}{' '}
      <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
        {accent}
      </span>
    </h2>
  );
}

function CheckList({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
          </span>
          <span className="text-base text-foreground/85 leading-relaxed">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ImageCard({ src, alt, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute -inset-4 bg-gradient-to-br from-primary/15 via-emerald-500/10 to-teal-500/15 blur-2xl rounded-full -z-10" />
      <img
        src={src}
        alt={alt}
        className="w-full max-w-[540px] mx-auto h-auto rounded-3xl border border-border shadow-2xl ring-1 ring-foreground/5"
      />
    </div>
  );
}

function ContactLine({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-primary" />
      </span>
      <span className="text-sm font-medium text-foreground">{text}</span>
    </div>
  );
}
